import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { io, type Socket } from 'socket.io-client';
import { Mic, MicOff, PhoneOff, Video, VideoOff } from 'lucide-react';
import { dateTime } from '@/lib/utils';
import { ApiError, post, session } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { useTranslation } from '@/lib/i18n';
import { translateServerMessage } from '@/lib/i18n-core';
import { visibleInterval } from '@/lib/polling';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Spinner } from '@/components/ui/primitives';
import { fetchIceServers, PeerConnection, type ConnectionQuality } from './webrtc';

const BASE = import.meta.env.VITE_API_URL ?? '/api/v1';
const WAITING_ROOM_REFRESH_MS = 60_000;

async function apiCall(tenant: string, path: string, body?: unknown) {
  const response = await fetch(`${BASE}${path}`, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', 'X-Tenant-Id': tenant },
    body: body ? JSON.stringify(body) : undefined,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error((payload as { error?: { message?: string } } | null)?.error?.message ?? '');
  }
  return payload;
}

function failureText(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return describeError(error);
  if (error instanceof Error && error.message) return translateServerMessage(error.message) ?? error.message;
  return fallback;
}

interface Room {
  id: string;
  status: string;
  provider: string;
  scheduledAt: string;
  patientName: string;
  doctorName: string | null;
  doctorDesignation: string | null;
  doctorPresent: boolean;
  patientPresent: boolean;
  endedAt: string | null;
}

/**
 * The tele-consultation waiting room (SRS 11.1).
 *
 * Reached by a link and nothing else: the token in the URL is the credential,
 * so this page never asks who you are and never shows a clinical record. It
 * shows who the consultation is with and whether the other side has arrived.
 */
export function TeleRoomPage() {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const who = params.get('as') === 'doctor' ? 'doctor' : 'patient';
  const [joined, setJoined] = useState(false);
  const { t } = useTranslation('portal');

  const linkTenant = params.get('tenant') ?? localStorage.getItem('his.portal.tenant') ?? window.location.hostname.split('.')[0];
  const tenant = who === 'doctor' ? (session.tenant() ?? linkTenant) : linkTenant;
  const staffSignedIn = who === 'patient' || Boolean(session.access());

  useEffect(() => {
    if (params.get('tenant')) localStorage.setItem('his.portal.tenant', params.get('tenant') as string);
  }, [params]);

  const room = useQuery({
    queryKey: ['tele-room', token],
    queryFn: () => apiCall(tenant, `/tele/rooms/${token}`) as Promise<Room>,
    refetchInterval: joined ? false : visibleInterval(WAITING_ROOM_REFRESH_MS),
    retry: false,
  });

  const join = useMutation({
    mutationFn: () =>
      who === 'doctor' ? post(`/tele/rooms/${token}/join/doctor`, {}) : apiCall(tenant, `/tele/rooms/${token}/join`, { who }),
    onSuccess: () => setJoined(true),
  });

  const teleCall = useTeleCall({
    tenant,
    token: token ?? '',
    who,
    active: joined && room.data?.provider === 'internal' && !room.data.endedAt,
    mediaErrorText: t('tele.media_error'),
  });

  if (room.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (room.isError) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <Alert variant="destructive" className="max-w-sm">
          {t('tele.invalid_link')}
        </Alert>
      </div>
    );
  }

  const data = room.data as Room;
  const otherSidePresent = who === 'patient' ? data.doctorPresent : data.patientPresent;
  const showStage = joined && data.provider === 'internal' && !data.endedAt && !teleCall.ended && !teleCall.refused;

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-8">
      <Card className={showStage ? 'w-full max-w-2xl' : 'w-full max-w-md'}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="h-5 w-5 text-primary" aria-hidden /> {t('tele.title')}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {who === 'patient' ? data.doctorName : data.patientName} · {dateTime(data.scheduledAt)}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {data.endedAt ? (
            <Alert>{t('tele.consultation_ended')}</Alert>
          ) : teleCall.refused ? (
            <Alert variant="destructive">{t('tele.doctor_refused')}</Alert>
          ) : teleCall.ended ? (
            <Alert>{t('tele.call_ended')}</Alert>
          ) : !staffSignedIn ? (
            <Alert variant="warning">{t('tele.doctor_sign_in')}</Alert>
          ) : !joined ? (
            <>
              <p className="text-sm text-muted-foreground">
                {who === 'patient' ? t('tele.join_hint_patient') : t('tele.join_hint_doctor')}
              </p>
              {otherSidePresent && (
                <p className="text-sm font-medium text-success" role="status">
                  {who === 'patient' ? t('tele.doctor_present') : t('tele.patient_present')}
                </p>
              )}
              {join.isError && <Alert variant="destructive">{failureText(join.error, t('tele.error'))}</Alert>}
              <Button className="w-full" onClick={() => join.mutate()} loading={join.isPending}>
                {t('tele.join_now')}
              </Button>
            </>
          ) : data.provider !== 'internal' ? (
            <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
              {t('tele.provider_pending', { provider: data.provider })}
            </div>
          ) : teleCall.mediaError ? (
            <Alert variant="destructive">{teleCall.mediaError}</Alert>
          ) : !teleCall.peerOnline ? (
            <Alert variant="warning">
              <span className="inline-flex items-center gap-2">
                <Spinner /> {who === 'patient' ? t('tele.waiting_for_doctor') : t('tele.waiting_for_patient')}
              </span>
            </Alert>
          ) : (
            <CallStage teleCall={teleCall} peerName={who === 'patient' ? data.doctorName : data.patientName} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function CallStage({ teleCall, peerName }: { teleCall: TeleCall; peerName: string | null }) {
  const { t } = useTranslation('portal');
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{peerName ?? t('tele.connecting')}</span>
        <div className="flex items-center gap-2">
          {teleCall.quality && teleCall.rtcState === 'connected' && (
            <Badge
              variant={teleCall.quality === 'good' ? 'success' : teleCall.quality === 'fair' ? 'warning' : 'destructive'}
            >
              {t(`tele.quality_${teleCall.quality}`)}
            </Badge>
          )}
          <Badge variant={teleCall.rtcState === 'connected' ? 'success' : 'warning'}>
            {teleCall.rtcState === 'connected'
              ? t('tele.connected')
              : teleCall.rtcState === 'reconnecting'
                ? t('tele.reconnecting')
                : t('tele.connecting')}
          </Badge>
        </div>
      </div>

      <div className="relative aspect-video overflow-hidden rounded-lg bg-black">
        <VideoTile stream={teleCall.remoteStream} className="h-full w-full" />
        {teleCall.rtcState !== 'connected' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <Spinner />
          </div>
        )}
        <div className="absolute bottom-3 end-3 aspect-video w-28 overflow-hidden rounded-md border-2 border-white/25 sm:w-40">
          <VideoTile stream={teleCall.localStream} muted mirrored className={`h-full w-full ${teleCall.camOn ? '' : 'opacity-40'}`} />
        </div>
      </div>

      <div className="flex items-center justify-center gap-3">
        <Button
          variant={teleCall.micOn ? 'outline' : 'secondary'}
          size="icon"
          onClick={teleCall.toggleMic}
          aria-label={t('tele.mute_microphone')}
          aria-pressed={!teleCall.micOn}
        >
          {teleCall.micOn ? <Mic className="h-4 w-4" aria-hidden /> : <MicOff className="h-4 w-4" aria-hidden />}
        </Button>
        <Button
          variant={teleCall.camOn ? 'outline' : 'secondary'}
          size="icon"
          onClick={teleCall.toggleCam}
          aria-label={t('tele.turn_camera_off')}
          aria-pressed={!teleCall.camOn}
        >
          {teleCall.camOn ? <Video className="h-4 w-4" aria-hidden /> : <VideoOff className="h-4 w-4" aria-hidden />}
        </Button>
        <Button variant="destructive" size="icon" onClick={teleCall.hangUp} aria-label={t('tele.end_call')}>
          <PhoneOff className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
}

function VideoTile({
  stream,
  muted,
  mirrored,
  className,
}: {
  stream: MediaStream | null;
  muted?: boolean;
  mirrored?: boolean;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) videoRef.current.srcObject = stream;
  }, [stream]);

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted={muted}
      className={`bg-black object-cover ${mirrored ? 'scale-x-[-1]' : ''} ${className ?? ''}`}
    />
  );
}

type TeleRtcState = 'idle' | 'connecting' | 'connected' | 'reconnecting';

interface TeleCall {
  rtcState: TeleRtcState;
  peerOnline: boolean;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  micOn: boolean;
  camOn: boolean;
  quality: ConnectionQuality | null;
  mediaError: string | null;
  ended: boolean;
  refused: boolean;
  toggleMic: () => void;
  toggleCam: () => void;
  hangUp: () => void;
}

const GIVE_UP_AFTER_MS = 20_000;

function useTeleCall({
  tenant,
  token,
  who,
  active,
  mediaErrorText,
}: {
  tenant: string;
  token: string;
  who: 'patient' | 'doctor';
  active: boolean;
  mediaErrorText: string;
}): TeleCall {
  const [rtcState, setRtcState] = useState<TeleRtcState>('idle');
  const [peerOnline, setPeerOnline] = useState(false);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [quality, setQuality] = useState<ConnectionQuality | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [ended, setEnded] = useState(false);
  const [refused, setRefused] = useState(false);

  const socketRef = useRef<Socket | null>(null);
  const peerRef = useRef<PeerConnection | null>(null);
  const mediaErrorRef = useRef(mediaErrorText);

  useEffect(() => {
    mediaErrorRef.current = mediaErrorText;
  }, [mediaErrorText]);

  useEffect(() => {
    if (!active) return undefined;

    let cancelled = false;
    let stream: MediaStream | null = null;
    let qualityTimer: ReturnType<typeof setInterval> | null = null;
    let giveUpTimer: ReturnType<typeof setTimeout> | null = null;

    const stopQualityPolling = () => {
      if (qualityTimer) clearInterval(qualityTimer);
      qualityTimer = null;
    };

    const clearGiveUp = () => {
      if (giveUpTimer) clearTimeout(giveUpTimer);
      giveUpTimer = null;
    };

    const armGiveUp = () => {
      clearGiveUp();
      giveUpTimer = setTimeout(() => setEnded(true), GIVE_UP_AFTER_MS);
    };

    const startQualityPolling = () => {
      stopQualityPolling();
      qualityTimer = setInterval(() => {
        if (document.visibilityState !== 'visible') return;
        void peerRef.current?.getQuality().then((q) => {
          if (q) setQuality(q);
        });
      }, 4000);
    };

    async function startPeer(mediaStream: MediaStream, socket: Socket) {
      const iceServers = await fetchIceServers(() => apiCall(tenant, '/tele/webrtc-config'));
      if (cancelled) return null;
      peerRef.current?.close();
      const connection = new PeerConnection(iceServers, mediaStream, {
        onIceCandidate: (candidate) => socket.emit('webrtc:ice-candidate', candidate),
        onRemoteStream: (remote) => setRemoteStream(remote),
        onConnectionState: (state) => {
          if (state === 'connected') {
            setRtcState('connected');
            clearGiveUp();
            startQualityPolling();
          } else if (state === 'disconnected' || state === 'failed') {
            setRtcState('reconnecting');
            armGiveUp();
            if (state === 'failed' && who === 'doctor' && peerRef.current) {
              void peerRef.current.createOffer(true).then((sdp) => socket.emit('webrtc:offer', sdp));
            }
          } else {
            setRtcState('connecting');
          }
        },
      });
      peerRef.current = connection;
      return connection;
    }

    async function start() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      } catch {
        if (!cancelled) setMediaError(mediaErrorRef.current);
        return;
      }
      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      setLocalStream(stream);
      const activeStream = stream;

      const socket = io('/tele', {
        auth: (callback) =>
          callback(who === 'doctor' ? { tenant, roomToken: token, who, token: session.access() ?? '' } : { tenant, roomToken: token, who }),
      });
      socketRef.current = socket;

      socket.on('tele:refused', () => setRefused(true));

      socket.on('tele:peer-online', () => {
        setPeerOnline(true);
        if (who === 'doctor') {
          void (async () => {
            const connection = await startPeer(activeStream, socket);
            if (!connection) return;
            const sdp = await connection.createOffer();
            socket.emit('webrtc:offer', sdp);
          })();
        }
      });

      socket.on('tele:peer-offline', () => {
        setPeerOnline(false);
        setRtcState((current) => (current === 'connected' ? 'reconnecting' : current));
        armGiveUp();
      });

      socket.on('tele:ended', () => setEnded(true));

      socket.on('webrtc:offer', (sdp: RTCSessionDescriptionInit) => {
        void (async () => {
          const connection = peerRef.current ?? (await startPeer(activeStream, socket));
          if (!connection) return;
          const answer = await connection.handleOffer(sdp);
          socket.emit('webrtc:answer', answer);
        })();
      });

      socket.on('webrtc:answer', (sdp: RTCSessionDescriptionInit) => {
        void peerRef.current?.handleAnswer(sdp);
      });

      socket.on('webrtc:ice-candidate', (candidate: RTCIceCandidateInit) => {
        void peerRef.current?.addIceCandidate(candidate);
      });
    }

    void start();

    return () => {
      cancelled = true;
      clearGiveUp();
      stopQualityPolling();
      socketRef.current?.disconnect();
      socketRef.current = null;
      peerRef.current?.close();
      peerRef.current = null;
      stream?.getTracks().forEach((t) => t.stop());
      setLocalStream(null);
      setRemoteStream(null);
      setRtcState('idle');
      setPeerOnline(false);
      setQuality(null);
    };
  }, [active, tenant, token, who]);

  const toggleMic = () => {
    const next = !micOn;
    localStream?.getAudioTracks().forEach((t) => (t.enabled = next));
    setMicOn(next);
  };

  const toggleCam = () => {
    const next = !camOn;
    localStream?.getVideoTracks().forEach((t) => (t.enabled = next));
    setCamOn(next);
  };

  const hangUp = () => {
    socketRef.current?.emit('tele:leave');
    setEnded(true);
  };

  return {
    rtcState,
    peerOnline,
    localStream,
    remoteStream,
    micOn,
    camOn,
    quality,
    mediaError,
    ended,
    refused,
    toggleMic,
    toggleCam,
    hangUp,
  };
}
